import { useCallback, useEffect, useRef, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";
import type { PublicKeyCredentialRequestOptionsJSON } from "@simplewebauthn/browser";
import { apiPost, errorMessage } from "../api";
import { AppHQLogo } from "../Logo";

/**
 * Passkey login. Key iOS resilience (from the FM Financieel reference): prefetch the login
 * options at mount so the click handler can call startAuthentication() SYNCHRONOUSLY within the
 * tap's transient activation — otherwise iOS Safari silently drops the ceremony on slow links.
 */
export function Login() {
  const [options, setOptions] = useState<PublicKeyCredentialRequestOptionsJSON | null>(null);
  const [status, setStatus] = useState<"idle" | "busy" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const fetching = useRef(false);

  const prefetch = useCallback(async () => {
    if (fetching.current) return;
    fetching.current = true;
    try {
      const r = await apiPost("/api/auth/login/options");
      if (r.ok) setOptions(((await r.json()) as { options: PublicKeyCredentialRequestOptionsJSON }).options);
    } catch {
      /* ignore */
    } finally {
      fetching.current = false;
    }
  }, []);

  useEffect(() => {
    void prefetch();
  }, [prefetch]);

  async function handleLogin() {
    setStatus("busy");
    setError(null);
    try {
      let opts = options;
      let authPromise: ReturnType<typeof startAuthentication>;
      if (opts) {
        authPromise = startAuthentication({ optionsJSON: opts }); // synchronous — iOS activation
      } else {
        const r = await apiPost("/api/auth/login/options");
        if (!r.ok) throw new Error(await errorMessage(r, "Kon niet starten."));
        opts = ((await r.json()) as { options: PublicKeyCredentialRequestOptionsJSON }).options;
        authPromise = startAuthentication({ optionsJSON: opts });
      }
      const response = await authPromise;
      const v = await apiPost("/api/auth/login/verify", { response });
      if (!v.ok) throw new Error(await errorMessage(v, "Kon niet inloggen."));
      window.location.href = "/";
    } catch (e) {
      const cancelled = e instanceof Error && e.name === "NotAllowedError";
      setStatus("error");
      setError(cancelled ? "Geannuleerd of verlopen. Probeer het opnieuw." : e instanceof Error ? e.message : "Er ging iets mis.");
      void prefetch();
    }
  }

  return (
    <div className="auth-screen">
      <div className="glass-card">
        <div className="logo-wrap">
          <AppHQLogo size={72} />
        </div>
        <h1>AppHQ</h1>
        <p className="sub">Log in met je passkey.</p>
        <button className="btn btn-primary" onClick={handleLogin} disabled={status === "busy"}>
          {status === "busy" ? <span className="spinner" /> : <KeyIcon />}
          {status === "busy" ? "Bevestig met Face ID of Touch ID…" : "Doorgaan met passkey"}
        </button>
        {error && <p className="error">{error}</p>}
        <p className="foot">Privé-omgeving · geen wachtwoord, geen registratie</p>
      </div>
    </div>
  );
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
      <rect x="5" y="11" width="14" height="9" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}
